import React, { useState } from "react";
import QuillEditor from "@/modules/Email/components/QuillEditor";
import { Input } from "@/components/form";

type Props = {
  employeeName: string;
  roleName: string;
  initialTo?: string;
  initialCc?: string;
  initialBcc?: string;
  initialSubject?: string;
  initialContent?: string;
  sentDate?: string;
  sentTime?: string;
  viewMode?: boolean;
  emailStatus?: string;
  onCancel?: () => void;
  onSendNow: (payload: {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    content: string;
  }) => void;
  onSendLater: (payload: {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    content: string;
  }) => void;
  sendLaterLabel?: string;
  introText?: string;
};

const SendEmployeeEmailForm: React.FC<Props> = ({
  employeeName,
  roleName,
  initialTo,
  initialCc,
  initialBcc,
  initialSubject,
  initialContent,
  sentDate,
  sentTime,
  viewMode = false,
  emailStatus,
  onCancel,
  onSendNow,
  onSendLater,
  sendLaterLabel,
  introText,
}) => {
  const [to, setTo] = useState(initialTo || "");
  const [cc, setCc] = useState(initialCc || "");
  const [bcc, setBcc] = useState(initialBcc || "");
  const [subject, setSubject] = useState(initialSubject || "");
  const [content, setContent] = useState(initialContent || "");
  const [toError, setToError] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [contentError, setContentError] = useState("");
  const [sending, setSending] = useState<"now" | "later" | null>(null);

  const validate = () => {
    let ok = true;
    const emailRe = /[^\s@]+@[^\s@]+\.[^\s@]+/;
    const toList = (to || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (!toList.length || !toList.every((e) => emailRe.test(e))) {
      setToError("Enter valid recipient emails");
      ok = false;
    } else {
      setToError("");
    }
    if (!subject.trim()) {
      setSubjectError("Subject is required");
      ok = false;
    } else {
      setSubjectError("");
    }
    if (!content || !content.replace(/<(.|\n)*?>/g, "").trim()) {
      setContentError("Email content is required");
      ok = false;
    } else {
      setContentError("");
    }
    return ok;
  };

  return (
    <div className="w-full">
      <div className="px-6 pt-4">
        <p className="text-md font-medium">
          {introText || "User added successfully! Would you like to send them a onboard email now?"}
        </p>
        <p className="text-sm text-[#6C6C6C] mt-3 mb-2" ><span className="text-[#DC2626]">*</span>indicates mandatory fields</p>
      </div>

      <div className="px-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input id="employeeName" label="Employee name" value={employeeName} disabled />
        <Input id="roleName" label="Role" value={roleName} disabled />
      </div>

      {(sentDate || sentTime) && (
        <div className="px-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {sentDate && <Input id="sentDate" label="Sent date" value={sentDate} disabled />}
          {sentTime && <Input id="sentTime" label="Sent time" value={sentTime} disabled />}
        </div>
      )}

      <div className="px-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Input
            id="to"
            label="To"
            required
            placeholder="Enter recipients"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            error={toError}
            disabled={viewMode}
          />
        </div>
        <Input
          id="cc"
          label="Cc"
          placeholder="Enter cc recipients"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          disabled={viewMode}
        />
        <Input
          id="bcc"
          label="Bcc"
          placeholder="Enter bcc recipients"
          value={bcc}
          onChange={(e) => setBcc(e.target.value)}
          disabled={viewMode}
        />
      </div>

      <div className="px-6">
        <Input
          id="subject"
          label="Subject"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          error={subjectError}
          // disabled={viewMode}
        />
      </div>

      <div className={viewMode ? "px-6 mb-4" : "px-6"}>
        <fieldset className="border-0 p-0 m-0">
          <legend className="block text-[#1E1E1E] text-[14px] font-[500] mb-1">
            Email content<span className="text-[#DC2626]">*</span>
          </legend>
          <div className="border border-gray-200 rounded-[6px] overflow-hidden">
            <QuillEditor value={content} 
            onChange={setContent}
             placeholder="Type your email..." 
             // readOnly={viewMode}
             />
          </div>
          {contentError && (
            <p className="mt-1 text-[12px] font-[500] text-[#FF3B30]">{contentError}</p>
          )}
        </fieldset>
      </div>

      {!viewMode && (
        emailStatus === "pending" ? (
          <div className="px-6 py-4 flex justify-end gap-2 border-t border-gray-100">
            <button
              onClick={() => onCancel && onCancel()}
              disabled={!!sending}
              className="h-[38px] cursor-pointer px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!validate()) return;
                setSending("now");
                const toList = (to || "").split(",").map((s) => s.trim()).filter(Boolean);
                const ccList = (cc || "").split(",").map((s) => s.trim()).filter(Boolean);
                const bccList = (bcc || "").split(",").map((s) => s.trim()).filter(Boolean);
                onSendNow({ to: toList, cc: ccList, bcc: bccList, subject, content });
              }}
              disabled={!!sending}
              className="h-[38px] cursor-pointer px-4 text-sm text-white bg-[#73B5C5] rounded-[6px] hover:bg-[#5da7b8] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending === "now" ? "Sending..." : "Send email"}
            </button>
          </div>
        ) : (
          <div className="px-6 py-4 flex justify-end gap-2 border-t border-gray-100">
            <button
              onClick={() => {
                if (!validate()) return;
                setSending("later");
                const toList = (to || "").split(",").map((s) => s.trim()).filter(Boolean);
                const ccList = (cc || "").split(",").map((s) => s.trim()).filter(Boolean);
                const bccList = (bcc || "").split(",").map((s) => s.trim()).filter(Boolean);
                onSendLater({ to: toList, cc: ccList, bcc: bccList, subject, content });
              }}
              disabled={!!sending}
              className="h-[38px] cursor-pointer px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending === "later" ? "Sending..." : (sendLaterLabel || "Send later")}
            </button>
            <button
              onClick={() => {
                if (!validate()) return;
                setSending("now");
                const toList = (to || "").split(",").map((s) => s.trim()).filter(Boolean);
                const ccList = (cc || "").split(",").map((s) => s.trim()).filter(Boolean);
                const bccList = (bcc || "").split(",").map((s) => s.trim()).filter(Boolean);
                onSendNow({ to: toList, cc: ccList, bcc: bccList, subject, content });
              }}
              disabled={!!sending}
              className="h-[38px] cursor-pointer px-4 text-sm text-white bg-[#73B5C5] rounded-[6px] hover:bg-[#5da7b8] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending === "now" ? "Sending..." : "Send email now"}
            </button>
          </div>
        )
      )}
    </div>
  );
};

export default SendEmployeeEmailForm;
